import 'src/styles/ui/list-table.css';

import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import { Col, ListGroup, ListGroupItem, Row } from 'react-bootstrap';

/**
 * ListTable Component - Flexible collapsible table with header
 *
 * @param {Object} props
 * @param {Object} props.icon - FontAwesome icon for the header (optional)
 * @param {boolean} props.boxedIcon - Render the icon inside a white rounded box
 * @param {React.ReactNode} props.headerContent - Custom header content (title, info, etc.)
 * @param {string} props.title - Simple header title (alternative to headerContent)
 * @param {number} props.count - Optional circular counter shown next to the title
 * @param {boolean} props.isExpanded - Whether the table is expanded
 * @param {boolean} props.isHighlighted - Whether to highlight the table (e.g., scanned state)
 * @param {Function} props.onToggle - Callback when header is clicked to toggle
 * @param {Array} props.columns - Array of column definitions: [{ label: string, width: number (1-12) }]
 * @param {React.ReactNode} props.children - Table rows content
 * @param {React.ReactNode} props.footer - Optional footer content (e.g., "Load more" button)
 * @param {string} props.className - Additional CSS classes
 */
const ListTable = ({
  icon,
  boxedIcon,
  headerContent,
  title,
  count,
  isExpanded,
  isHighlighted,
  onToggle,
  columns,
  children,
  footer,
  className,
  ...props
}) => {
  // Simple title/count header (used when no custom headerContent is passed)
  const resolvedHeaderContent = headerContent || (
    <div className='list-table__header-title'>
      <span className='list-table__title-text'>{title}</span>
      {count != null && <span className='list-table__count'>{count}</span>}
    </div>
  );
  const collapsedClass = classNames('list-table__header--collapsed', {
    'list-table__header--highlighted': isHighlighted,
  });

  const expandedHeaderClass = classNames('list-table__header--expanded', {
    'list-table__header--highlighted': isHighlighted,
  });

  const tableBodyClass = classNames('list-table__body', {
    'list-table__body--highlighted': isHighlighted,
  });

  const wrapperClass = classNames('list-table', className);

  // Collapsed view
  if (!isExpanded) {
    return (
      <div className={wrapperClass} {...props}>
        <div className={collapsedClass} onClick={onToggle}>
          <div className='list-table__header-inner'>
            {icon && (
              <div
                className={classNames('list-table__icon', { 'list-table__icon--boxed': boxedIcon })}
              >
                <FontAwesomeIcon icon={icon} />
              </div>
            )}
            {resolvedHeaderContent}
            <div className='list-table__chevron'>
              <FontAwesomeIcon icon={faChevronDown} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Expanded view
  return (
    <div className={wrapperClass} {...props}>
      <div className={expandedHeaderClass} onClick={onToggle}>
        <div className='list-table__header-inner'>
          {icon && (
            <div
              className={classNames('list-table__icon', { 'list-table__icon--boxed': boxedIcon })}
            >
              <FontAwesomeIcon icon={icon} />
            </div>
          )}
          {resolvedHeaderContent}
          <div className='list-table__chevron'>
            <FontAwesomeIcon icon={faChevronUp} />
          </div>
        </div>
      </div>

      <div className={tableBodyClass}>
        <ListGroup>
          {/* Column Headers */}
          {columns && columns.length > 0 && (
            <ListGroupItem className='list-table__columns'>
              <Row>
                {columns.map(col => (
                  <Col key={col.label} xs={col.width || Math.floor(12 / columns.length)}>
                    {col.label}
                  </Col>
                ))}
              </Row>
            </ListGroupItem>
          )}

          {/* Table Rows */}
          {children}
        </ListGroup>

        {/* Footer (e.g., Load more button) */}
        {footer && <div className='list-table__footer'>{footer}</div>}
      </div>
    </div>
  );
};

ListTable.propTypes = {
  icon: PropTypes.object,
  boxedIcon: PropTypes.bool,
  headerContent: PropTypes.node,
  title: PropTypes.string,
  count: PropTypes.number,
  isExpanded: PropTypes.bool.isRequired,
  isHighlighted: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      width: PropTypes.number, // Bootstrap grid width (1-12)
    })
  ),
  children: PropTypes.node,
  footer: PropTypes.node,
  className: PropTypes.string,
};

ListTable.defaultProps = {
  icon: null,
  boxedIcon: false,
  headerContent: null,
  title: '',
  count: null,
  isHighlighted: false,
  columns: [],
  children: null,
  footer: null,
  className: '',
};

export default ListTable;
